export interface TokenStore {
  get(): string | null | Promise<string | null>;
  set(token: string): void | Promise<void>;
  clear(): void | Promise<void>;
}

export const memoryTokenStore = (): TokenStore => {
  let token: string | null = null;
  return {
    get: () => token,
    set: (t) => {
      token = t;
    },
    clear: () => {
      token = null;
    },
  };
};

/**
 * Minimal shape of the Electron preload bridge that this adapter depends on.
 * Defined locally (rather than imported from `apps/desktop/src/preload`) so
 * the shared package stays free of a desktop dependency — the renderer
 * resolves it at runtime via `window.fluxter`.
 */
interface DesktopAuthBridge {
  getAuthToken: () => Promise<string | null>;
  setAuthToken: (token: string) => Promise<void>;
  clearAuthToken: () => Promise<void>;
}

const resolveBridge = (): DesktopAuthBridge => {
  const bridge = (globalThis as { window?: { fluxter?: Partial<DesktopAuthBridge> } }).window
    ?.fluxter;
  if (
    !bridge ||
    typeof bridge.getAuthToken !== 'function' ||
    typeof bridge.setAuthToken !== 'function' ||
    typeof bridge.clearAuthToken !== 'function'
  ) {
    throw new Error(
      'desktopTokenStore: window.fluxter bridge is not available. ' +
        'This store can only be used inside the Electron renderer.',
    );
  }
  return bridge as DesktopAuthBridge;
};

/**
 * Token store backed by the Electron main process, where the bytes are
 * persisted via `safeStorage` (OS keychain). All three methods are async
 * round-trips over IPC; callers should `await` them.
 *
 * Reads are intentionally not memoized here — the API client only reads
 * the token once per request anyway, and avoiding cache means logout
 * (in another renderer or via the menu) takes effect immediately on the
 * next call instead of after a manual cache invalidation step.
 */
export const desktopTokenStore = (): TokenStore => {
  const bridge = resolveBridge();
  return {
    get: () => bridge.getAuthToken(),
    set: (token) => bridge.setAuthToken(token),
    clear: () => bridge.clearAuthToken(),
  };
};
